/**
 * Types du noyau d'élection.
 *
 * Tout `src/core` n'importe ni le DOM ni le réseau : ce code est exécutable et
 * testable seul, et peut être extrait dans `packages/core` sans modification
 * (SPECIFICATION.md §14). Toute règle du §6 de la spécification est
 * représentée ici par un type, jamais par une convention implicite.
 */

/** Phases du cycle de vie (SPECIFICATION.md §5.1). */
export type Phase =
  | 'BROUILLON'
  | 'CANDIDATURES'
  | 'VALIDATION'
  | 'TOUR1'
  | 'DEPOUILLEMENT1'
  | 'TOUR2'
  | 'DEPOUILLEMENT2'
  | 'DEPARTAGE'
  | 'ACCEPTATION'
  | 'DESIGNATION'
  | 'PROCLAMATION';

/** Ordre du cycle de vie, pour l'affichage et les gardes de transition. */
export const PHASES: readonly Phase[] = [
  'BROUILLON',
  'CANDIDATURES',
  'VALIDATION',
  'TOUR1',
  'DEPOUILLEMENT1',
  'TOUR2',
  'DEPOUILLEMENT2',
  'DEPARTAGE',
  'ACCEPTATION',
  'DESIGNATION',
  'PROCLAMATION',
];

/** Libellés destinés à l'interface (enseignant et projection). */
export const PHASE_LABEL: Readonly<Record<Phase, string>> = {
  BROUILLON: 'Préparation',
  CANDIDATURES: 'Candidatures ouvertes',
  VALIDATION: 'Validation des candidatures',
  TOUR1: 'Premier tour — vote en cours',
  DEPOUILLEMENT1: 'Dépouillement du premier tour',
  TOUR2: 'Second tour — vote en cours',
  DEPOUILLEMENT2: 'Dépouillement du second tour',
  DEPARTAGE: 'Départage',
  ACCEPTATION: 'Acceptation du mandat',
  DESIGNATION: 'Désignation des suppléants',
  PROCLAMATION: 'Proclamation des résultats',
};

/** Une personne de la liste de classe. Aucune donnée d'âge n'est collectée (O2). */
export interface Person {
  readonly id: string;
  readonly prenom: string;
  readonly nom: string;
  /** Libellé public, attribué selon D5 : « Prénom », « Prénom N. » ou « Prénom N. (2) ». */
  readonly label: string;
  /** Rang d'import, garantit un ordre d'affichage stable et déterministe. */
  readonly ordre: number;
}

/** Nature d'un item soumis au vote (D15). */
export type ItemKind = 'binome' | 'individu';

/** Objet d'une voix : un binôme déclaré, ou une personne votable seule. */
export interface Item {
  readonly id: string;
  readonly kind: ItemKind;
  readonly titulaireId: string;
  /** Absent pour un individu voté seul (« vote sans suppléant », O8). */
  readonly suppleantId?: string;
  readonly label: string;
}

/** Paramètres de scrutin. Les valeurs par défaut sont celles de F1370. */
export interface Rules {
  readonly titulaires: number;
  readonly suppleants: number;
  readonly voixTour1: number;
  readonly eligibilite: 'liste_classe' | 'candidats';
  readonly acceptationObligatoire: boolean;
}

export const DEFAULT_RULES: Rules = {
  titulaires: 2,
  suppleants: 2,
  voixTour1: 2,
  eligibilite: 'liste_classe',
  acceptationObligatoire: true,
};

export interface ElectionConfig {
  readonly nom: string;
  readonly classe: string;
  readonly etablissement: string;
  /** Date de l'élection, format AAAA-MM-JJ. Sert au calcul de la purge J+7. */
  readonly dateISO: string;
  readonly rules: Rules;
}

/**
 * Un bulletin, tel qu'enregistré dans le journal.
 *
 * `tokenHash` est en clair : il permet de rejeter un doublon sans connaître
 * l'électeur. Le choix, lui, n'est jamais nominatif.
 */
export interface BallotRecord {
  readonly tokenHash: string;
  readonly choices: readonly string[];
  readonly blank: boolean;
  readonly round: 1 | 2;
  readonly ts: number;
}

export type SeatRole = 'titulaire' | 'suppleant';

/**
 * Origine d'un siège :
 * - `elu` : élu au scrutin ;
 * - `designe` : suppléant désigné après le dépouillement (O8) ;
 * - `force` : décision souveraine de l'enseignant (§6.8).
 *
 * Il n'existe aucune valeur « élu sans vote » : un second tour a toujours lieu
 * dès qu'un siège reste à pourvoir, même si un seul item demeure en lice.
 */
export type SeatVia = 'elu' | 'designe' | 'force';

export interface Seat {
  readonly role: SeatRole;
  readonly personId: string;
  readonly itemId?: string;
  readonly via: SeatVia;
}

/** Motif de rejet d'un bulletin (SPECIFICATION.md §6.1). */
export type NulReason =
  | 'item_inconnu'
  | 'item_repete'
  | 'voix_en_exces'
  | 'jeton_double';

export const NUL_REASON_LABEL: Readonly<Record<NulReason, string>> = {
  item_inconnu: 'item inconnu',
  item_repete: 'même item désigné deux fois',
  voix_en_exces: 'plus de voix que permis',
  jeton_double: 'jeton ayant déjà voté pour ce tour',
};

/** Décision de départage d'une égalité (D4, §6.4). */
export interface TiebreakDecision {
  readonly round: 1 | 2;
  readonly tiedItemIds: readonly string[];
  readonly chosenItemId: string;
  readonly motif: string;
  readonly ts: number;
}

/** Acceptation ou refus de mandat d'une personne élue sans s'être portée candidate (O3). */
export interface MandateDecision {
  readonly personId: string;
  readonly accepted: boolean;
  readonly motif: string;
  readonly ts: number;
}

/** Désignation du suppléant d'un individu élu titulaire (O8, phase DESIGNATION). */
export interface Designation {
  readonly titulaireId: string;
  readonly suppleantId: string;
  readonly motif: string;
  readonly ts: number;
}

/** Acte souverain de l'enseignant (§6.8). */
export type OverrideKind = 'validation' | 'modification';

export interface OverrideAct {
  readonly kind: OverrideKind;
  /** Sièges retenus. Vide pour une simple validation. */
  readonly seats: readonly Seat[];
  readonly motif: string;
  /** Empreinte du résultat calculé au moment de l'acte : rend la substitution vérifiable. */
  readonly computedFingerprint: string;
  readonly phase: Phase;
  readonly ts: number;
}

/** Résultat d'un tour de scrutin. */
export interface RoundResult {
  readonly round: 1 | 2;
  readonly voixParElecteur: number;
  readonly inscrits: number;
  /** Votants : jetons distincts ayant déposé un bulletin valide, blanc inclus. */
  readonly votants: number;
  /** Bulletins valides (complets ou partiels) : base de la majorité absolue. */
  readonly valides: number;
  readonly complets: number;
  readonly partiels: number;
  readonly blancs: number;
  readonly nuls: number;
  readonly nulsParMotif: Readonly<Record<string, number>>;
  /** Classement décroissant, départage d'affichage stable par libellé. */
  readonly classement: readonly VoixItem[];
  /** Items atteignant la majorité absolue (tour 1 uniquement). */
  readonly majoritaires: readonly string[];
  /** Seuil de voix à strictement dépasser, ou `null` au second tour. */
  readonly seuil: number | null;
  readonly totalVoix: number;
}

export interface VoixItem {
  readonly itemId: string;
  readonly label: string;
  readonly voix: number;
}

/** Types d'événements du journal (SPECIFICATION.md §8.2). */
export type EventType =
  | 'election.created'
  | 'roster.imported'
  | 'codes.generated'
  | 'candidate.declared'
  | 'candidate.removed'
  | 'phase.changed'
  | 'tour2.validated'
  | 'ballot.cast'
  | 'tiebreak.decided'
  | 'mandate.decided'
  | 'suppleant.designated'
  | 'result.validated'
  | 'result.overridden'
  /**
   * Publication, par le bureau, de ce que les élèves ont le droit de voir.
   * Cet événement ne modifie pas l'état de l'élection : il l'expose.
   */
  | 'public.state';

/**
 * Événement du journal, immuable et chaîné.
 *
 * `counter` est monotone **par instance émettrice** et `prevHash` chaîne les
 * événements de cette même instance : deux instances peuvent donc écrire en
 * parallèle sans jamais se contredire.
 */
export interface JournalEvent {
  readonly id: string;
  readonly electionId: string;
  readonly type: EventType;
  readonly payload: unknown;
  readonly ts: number;
  readonly origin: string;
  readonly counter: number;
  readonly prevHash: string;
  readonly hash: string;
}

